import { SyntheticEvent } from 'react';

import { useField } from 'react-final-form';

import {
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Typography,
} from '@cast/design-system';

import { ConstraintMode } from '../../../_types';
import {
  getWorkloadConstraintsMaxInputProps,
  getWorkloadConstraintsMinInputProps,
} from '../../../_utils';
import { InputPairContainer } from '../InputPairContainer';
import { ResourceInput, ResourceInputProps } from '../Resource/ResourceInput';
import { SettingsInfoCallout } from '../SettingsInfoCallout';

import { useControlsGroupState } from '@cast/shared-rff-ui';

type ResourceType = 'cpu' | 'memory';

type WorkloadConstraintsFieldV2Props = Pick<
  ResourceInputProps,
  'LabelComponent'
>;

const ResourceConstraintsGroup = ({
  resource,
  LabelComponent,
}: {
  resource: ResourceType;
  LabelComponent: ResourceInputProps['LabelComponent'];
}) => {
  const { name: modeName = `` } = useControlsGroupState({
    name: `${resource}.mode`,
  });
  const { input } = useField<ConstraintMode>(modeName);

  const handleModeChange = (
    _: SyntheticEvent,
    value: ConstraintMode | null
  ) => {
    if (value) {
      input.onChange(value);
    }
  };

  const isPercentage = input.value === 'percentage';
  const percentageOverride = isPercentage ? { endAdornment: '%' } : {};
  const resourceLabel = resource === 'cpu' ? 'CPU' : 'memory';

  return (
    <Stack gap={8}>
      <Typography variant="P12B">
        {resource === 'cpu' ? 'CPU:' : 'Memory:'}
      </Typography>
      <ToggleButtonGroup
        value={input.value}
        onChange={handleModeChange}
        size="sm"
        exclusive
        testId={`${resource}-constraint-mode-toggle`}
      >
        <ToggleButton
          value="constant"
          text="Value"
          testId={`${resource}-constraint-mode-constant`}
        />
        <ToggleButton
          value="percentage"
          text="% of request"
          testId={`${resource}-constraint-mode-percentage`}
        />
      </ToggleButtonGroup>
      <InputPairContainer>
        <ResourceInput
          resource={resource}
          name="min"
          variant="default"
          LabelComponent={LabelComponent}
          emtyValueOnDisabledResource
          size="medium"
          {...getWorkloadConstraintsMinInputProps(resource)}
          {...percentageOverride}
        />
        <ResourceInput
          resource={resource}
          name="max"
          variant="default"
          LabelComponent={LabelComponent}
          emtyValueOnDisabledResource
          size="medium"
          {...getWorkloadConstraintsMaxInputProps(resource)}
          {...percentageOverride}
        />
      </InputPairContainer>
      {isPercentage && (
        <SettingsInfoCallout>
          If the workload does not have defined {resourceLabel} requests, they
          will be treated as having no limit set.
        </SettingsInfoCallout>
      )}
    </Stack>
  );
};

export const WorkloadConstraintsFieldV2 = ({
  LabelComponent,
}: WorkloadConstraintsFieldV2Props) => {
  return (
    <Stack gap={26} data-testid="workload-constraints-field">
      <ResourceConstraintsGroup
        resource="cpu"
        LabelComponent={LabelComponent}
      />
      <ResourceConstraintsGroup
        resource="memory"
        LabelComponent={LabelComponent}
      />
    </Stack>
  );
};
